/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { Vulnerability, FilterState, VulnerabilityFilters } from '../types/vulnerability';
import { VulnerabilityDataProcessor } from '../utils/dataProcessor';
import { useUserPreferences } from './UserPreferencesContext';

interface VulnerabilityContextType {
  vulnerabilities: Vulnerability[];
  filteredVulnerabilities: Vulnerability[];
  filterState: FilterState;
  stats: ReturnType<typeof VulnerabilityDataProcessor.calculateStats>;
  setVulnerabilities: (vulns: Vulnerability[]) => void;
  updateFilters: (filters: Partial<FilterState>) => void;
  resetFilters: () => void;
  setAnalysisMode: (mode: FilterState['analysisMode']) => void;
  sortBy: (field: SortField, order?: 'asc' | 'desc') => void;
  sortField: SortField;
  sortOrder: 'asc' | 'desc';
  compareLimit: number;
  compareSelectionIds: string[];
  selectedForCompare: Vulnerability[];
  toggleCompareSelection: (id: string) => void;
  removeFromCompare: (id: string) => void;
  clearCompareSelection: () => void;
  isSelectedForCompare: (id: string) => boolean;
}

const VulnerabilityContext = createContext<VulnerabilityContextType | undefined>(undefined);

type SortField = 'id' | 'severity' | 'date' | 'package' | 'score';

const initialFilterState: FilterState = {
  analysisMode: 'all',
  searchQuery: '',
  severity: [],
  kaiStatus: [],
};

export function VulnerabilityProvider({ children }: { children: React.ReactNode }) {
  const { preferences } = useUserPreferences();
  const [vulnerabilities, setVulnerabilities] = useState<Vulnerability[]>([]);
  const [filterState, setFilterState] = useState<FilterState>({
    ...initialFilterState,
    analysisMode: preferences.defaultAnalysisMode,
  });
  const [sortField, setSortField] = useState<SortField>('severity');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [compareSelectionIds, setCompareSelectionIds] = useState<string[]>([]);

  const compareLimit = 4;

  useEffect(() => {
    setFilterState(prev => ({ ...prev, analysisMode: preferences.defaultAnalysisMode }));
  }, [preferences.defaultAnalysisMode]);

  // hydrate compare selection from localStorage once
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem('compareSelectionIds');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setCompareSelectionIds(parsed.slice(0, compareLimit));
        }
      } catch (error) {
        console.warn('Failed to parse stored compare selections', error);
        window.localStorage.removeItem('compareSelectionIds');
      }
    }
  }, [compareLimit]);

  // Apply filters to vulnerabilities
  const filteredVulnerabilities = useMemo(() => {
    let filtered = [...vulnerabilities];

    // Build filter object
    const filters: VulnerabilityFilters = {
      excludeInvalidNorisk: filterState.analysisMode === 'analysis',
      excludeAiInvalidNorisk: filterState.analysisMode === 'ai-analysis',
      searchQuery: filterState.searchQuery || undefined,
      severity: filterState.severity.length > 0 ? filterState.severity : undefined,
      kaiStatus: filterState.kaiStatus.length > 0 ? filterState.kaiStatus : undefined,
      dateRange: filterState.dateRange,
    };

    // Apply filters
    filtered = VulnerabilityDataProcessor.filter(filtered, filters);

    // Apply sorting
    filtered = VulnerabilityDataProcessor.sort(filtered, sortField, sortOrder);

    return filtered;
  }, [vulnerabilities, filterState, sortField, sortOrder]);

  // Calculate statistics
  const stats = useMemo(() => {
    const computed = VulnerabilityDataProcessor.calculateStats(filteredVulnerabilities);

    const totalInvalid = vulnerabilities.reduce((count, vuln) => (
      vuln.kaiStatus === 'invalid - norisk' ? count + 1 : count
    ), 0);
    const totalAiInvalid = vulnerabilities.reduce((count, vuln) => (
      vuln.kaiStatus === 'ai-invalid-norisk' ? count + 1 : count
    ), 0);

    computed.filteredOutInvalid = totalInvalid;
    computed.filteredOutAiInvalid = totalAiInvalid;
    computed.filteredPercentage = vulnerabilities.length === 0
      ? 0
      : (filteredVulnerabilities.length / vulnerabilities.length) * 100;

    return computed;
  }, [filteredVulnerabilities, vulnerabilities]);

  // keep compare selection in sync with latest vulnerability set
  useEffect(() => {
    if (vulnerabilities.length === 0) return;
    setCompareSelectionIds(prevIds => {
      if (prevIds.length === 0) return prevIds;
      const validIds = new Set(vulnerabilities.map(v => v.id));
      const filteredIds = prevIds.filter(id => validIds.has(id));
      if (filteredIds.length !== prevIds.length && typeof window !== 'undefined') {
        window.localStorage.setItem('compareSelectionIds', JSON.stringify(filteredIds));
      }
      return filteredIds;
    });
  }, [vulnerabilities]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('compareSelectionIds', JSON.stringify(compareSelectionIds));
  }, [compareSelectionIds]);

  const selectedForCompare = useMemo(() => {
    if (compareSelectionIds.length === 0) return [];
    const byId = new Map(vulnerabilities.map(v => [v.id, v]));
    return compareSelectionIds
      .map(id => byId.get(id))
      .filter((vulnerability): vulnerability is Vulnerability => Boolean(vulnerability));
  }, [compareSelectionIds, vulnerabilities]);

  const updateFilters = useCallback((newFilters: Partial<FilterState>) => {
    setFilterState(prev => ({ ...prev, ...newFilters }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilterState({
      ...initialFilterState,
      analysisMode: preferences.defaultAnalysisMode,
    });
    setSortField('severity');
    setSortOrder('desc');
  }, [preferences.defaultAnalysisMode]);

  const setAnalysisMode = useCallback((mode: FilterState['analysisMode']) => {
    setFilterState(prev => ({ ...prev, analysisMode: mode }));
  }, []);

  const sortBy = useCallback((field: SortField, order?: 'asc' | 'desc') => {
    setSortField(field);
    if (order) {
      setSortOrder(order);
    } else {
      // Toggle if same field, otherwise default to desc
      setSortOrder(prev => field === sortField ? (prev === 'asc' ? 'desc' : 'asc') : 'desc');
    }
  }, [sortField]);

  const toggleCompareSelection = useCallback((id: string) => {
    setCompareSelectionIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(existing => existing !== id);
      }
      if (prev.length >= compareLimit) {
        return prev;
      }
      return [...prev, id];
    });
  }, [compareLimit]);

  const removeFromCompare = useCallback((id: string) => {
    setCompareSelectionIds(prev => prev.filter(existing => existing !== id));
  }, []);

  const clearCompareSelection = useCallback(() => {
    setCompareSelectionIds([]);
  }, []);

  const isSelectedForCompare = useCallback((id: string) => compareSelectionIds.includes(id), [compareSelectionIds]);

  const value = useMemo(() => ({
    vulnerabilities,
    filteredVulnerabilities,
    filterState,
    stats,
    setVulnerabilities,
    updateFilters,
    resetFilters,
    setAnalysisMode,
    sortBy,
    sortField,
    sortOrder,
    compareLimit,
    compareSelectionIds,
    selectedForCompare,
    toggleCompareSelection,
    removeFromCompare,
    clearCompareSelection,
    isSelectedForCompare,
  }), [
    vulnerabilities,
    filteredVulnerabilities,
    filterState,
    stats,
    setVulnerabilities,
    updateFilters,
    resetFilters,
    setAnalysisMode,
    sortBy,
    sortField,
    sortOrder,
    compareLimit,
    compareSelectionIds,
    selectedForCompare,
    toggleCompareSelection,
    removeFromCompare,
    clearCompareSelection,
    isSelectedForCompare,
  ]);

  return (
    <VulnerabilityContext.Provider value={value}>
      {children}
    </VulnerabilityContext.Provider>
  );
}

export function useVulnerabilities() {
  const context = useContext(VulnerabilityContext);
  if (context === undefined) {
    throw new Error('useVulnerabilities must be used within a VulnerabilityProvider');
  }
  return context;
}

